import { useToast } from "@chakra-ui/react";
import {
  useMutation,
  UseMutationOptions,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosError } from "axios";

import { useEnvironment } from "@/common/contexts/EnvironmentContext";
import { UpsertForm } from "@/common/types/upsertForm";
import api from "@/common/utils/api";
import { segmentQueryKeys } from "@/company/data/segmentQueryKeys";
import { useCompaniesTableState } from "@/company/hooks/useCompaniesTableState";
import { ErrorResponse } from "@reflag/shared/api";
import { UIFilter } from "@reflag/shared/filter";
import { SegmentDTO } from "@reflag/shared/segmentAPI";

export const useSegmentUpsertMutations = (
  segment: SegmentDTO,
  companyFilter: UIFilter,
  options?: Omit<
    UseMutationOptions<SegmentDTO, AxiosError<ErrorResponse>, UpsertForm>,
    "mutationFn"
  >,
) => {
  const { app, env } = useEnvironment();
  const queryClient = useQueryClient();
  const toast = useToast();

  const { sorting, onSortingChange, columnStates, onColumnStatesChange } =
    useCompaniesTableState(segment);

  return useMutation({
    ...options,
    mutationFn: async (values: UpsertForm) => {
      if (values.action === "update") {
        return api
          .patch<"/apps/:appId/segments/:segmentId">(
            `/apps/${app.id}/segments/${segment.id}`,
            {
              companyFilter: !segment.isAllSegment ? companyFilter : undefined,
              columns: columnStates,
              columnSortKey: sorting[0]?.id,
              columnSortDesc: sorting[0]?.desc,
            },
          )
          .then((res) => res.data);
      } else {
        // Create a new segment
        return api
          .post<"/apps/:appId/segments">(`/apps/${app.id}/segments`, {
            name: values.name,
            companyFilter,
            columns: columnStates,
            columnSortKey: sorting[0]?.id,
            columnSortDesc: sorting[0]?.desc,
          })
          .then((res) => res.data);
      }
    },
    onSuccess: async (data, variables, context) => {
      toast({
        title:
          variables.action === "update"
            ? "Segment updated"
            : "New segment created",
        status: "success",
      });

      onSortingChange([]);
      onColumnStatesChange([]);

      // Wait for segments to be re-fetched
      await queryClient.invalidateQueries({
        queryKey: segmentQueryKeys.list(app.id),
      });
      await queryClient.invalidateQueries({
        queryKey: segmentQueryKeys.metadata(app.id),
      });
      await queryClient.invalidateQueries({
        queryKey: segmentQueryKeys.counts(app.id, env.id),
      });

      options?.onSuccess?.(data, variables, context);
    },
  });
};
